import type { IRequestWithdrawalResponse } from '#api';

interface IEstimatePayoutResponse {
  hasError: boolean;
  data: IEstimatePayout;
}

interface IPayPalInvoiceResponse {
  haserror: boolean;
  data: IPayPalInvoiceData;
}

export interface IRequestInvoiceResponse {
  haserror: boolean;
  message: string;
  data: IPayPalInvoiceResponse | ICryptoInvoiceData;
}

export interface ICurrenciesResponse {
  haserror: boolean;
  data: ICurrencyGroup[];
}

export interface ICurrencyData {
  subtitle: string;
  value: string;
  text: string;
  icon: string;
}

export interface IRequestInvoiceData {
  currency: string;
  amount?: number;
  method?: 'paypal' | 'kado';
}

export interface IPayPalInvoiceData {
  pay_amount: number;
  pay_currency: string;
  order_id: number;
}

export interface ICryptoInvoiceData {
  exchange_rate: string;
  expiration_estimate_date: string;
  minimal_payment_amount: number;
  order_id: string;
  pay_address: string;
  pay_currency: string;
  pay_amount: number;
}

export const ECashierTab = {
  Deposit: 'Deposit',
  Withdraw: 'Withdraw',
} as const;
export type TCashierTab = (typeof ECashierTab)[keyof typeof ECashierTab];

export const EDepositMethod = {
  Card: 'Card',
  Crypto: 'Crypto',
} as const;
export type TDepositMethod = (typeof EDepositMethod)[keyof typeof EDepositMethod];

export interface IEstimatePayout {
  currency_from: string;
  amount_from: number;
  currency_to: string;
  estimated_amount: number;
  amount_paid_to_user: number;
  network_fee: number;
  exchange_fee: number;
  min_amount: number;
  redemption_fee: number;
}

export interface IWithdrawalRequestData {
  send_address: string;
  amount: number;
  payment_currency: string;
}

export interface ICurrencyGroup {
  text: string;
  items: ICurrencyData[];
}

export const useCashierStore = defineStore('cashier', () => {
  const currencies = ref<ICurrencyGroup[]>([]);
  const cryptoCurrencies = computed<ICurrencyData[]>(
    () => currencies.value.find((group) => group.text === 'Crypto')?.items || [],
  );

  async function getCurrencies() {
    try {
      const { data } = await useTypedNuxtApp().$authApi.get<ICurrenciesResponse>('/currencies');
      currencies.value = data;
    } catch (error) {
      console.error(error);
    }
  }

  async function requestInvoice({ amount, currency, method }: IRequestInvoiceData) {
    try {
      const user = stores.user.getUser();
      if (!user) throw new Error('setLoggedIn: user is not logged in Firebase');

      const sessionKey = await stores.user.getIdToken();
      const { data, haserror, message } = await useTypedNuxtApp().$authApi.post<IRequestInvoiceResponse>(
        '/requestInvoice',
        { amount, currency, method, sessionKey },
      );

      if (haserror) {
        throw new Error(message || 'Failed to create invoice');
      }

      if ('pay_currency' in data) {
        return data;
      }

      return data.data;
    } catch (error: any) {
      console.error(error);
      return null;
    }
  }

  async function savePayPalOrderData({ orderId, foreignOrderId }: { orderId: string; foreignOrderId: number }) {
    try {
      const user = stores.user.getUser();
      if (!user) throw new Error('setLoggedIn: user is not logged in Firebase');

      const sessionKey = await stores.user.getIdToken();
      await useTypedNuxtApp().$authApi.post('/savePayPalOrderData', { orderId, foreignOrderId, sessionKey });
    } catch (error: any) {
      console.error(error);
    }
  }

  async function getEstimatePayout({ amount, currency }: { amount: number; currency: string }) {
    try {
      const user = stores.user.getUser();
      if (!user) throw new Error('setLoggedIn: user is not logged in Firebase');

      const sessionKey = await stores.user.getIdToken();
      const { hasError, data } = await useTypedNuxtApp().$authApi.post<IEstimatePayoutResponse>('/getEstimatePayout', {
        amount,
        currency,
        sessionKey,
      });

      if (hasError) {
        throw new Error('Failed to get estimate payout');
      }

      return data;
    } catch (error: any) {
      console.error(error);
    }
  }

  async function requestWithdrawal(data: IWithdrawalRequestData) {
    try {
      const user = stores.user.getUser();
      if (!user) throw new Error('setLoggedIn: user is not logged in Firebase');

      const sessionKey = await stores.user.getIdToken();
      const { send_address, amount, payment_currency } = data;

      return await useTypedNuxtApp().$authApi.post<IRequestWithdrawalResponse>('/requestWithdrawal', {
        send_address,
        amount,
        payment_currency,
        sessionKey,
      });
    } catch (error: any) {
      return {
        haserror: true,
        status: error?.data?.message || 'Failed to receive data from an external API',
      };
    }
  }

  return {
    currencies,
    cryptoCurrencies,
    getCurrencies,
    requestInvoice,
    savePayPalOrderData,
    getEstimatePayout,
    requestWithdrawal,
  };
});
